import type { ComponentType } from 'react'
import {
  IconeAlargador,
  IconeAproximar,
  IconeAfastar,
  IconeBloco,
  IconeCalco,
  IconeCortadorHidraulico,
  IconeExtensor,
  IconeHooligan,
  IconeMachadinha,
  IconeSerraSabre,
  IconeVStrut,
  IconeVidro,
  type PropsIcone,
} from '../icones'
import type { FerramentaOficinaCorte } from '../../dominio/didatico/oficinaCorte'
import type { ModoAlargador } from '../../dominio/didatico/interacaoOficina'

/**
 * Caixa de ferramentas da oficina de corte, num lugar só.
 *
 * Antes a mesma lista existia três vezes — trilha lateral, ações rápidas do
 * módulo e painel Ferramentas —, cada uma com rótulo e ícone próprios, e as
 * três discordavam: o extensor aparecia desabilitado na trilha depois de o
 * fluxo dele já estar implementado, e as ferramentas de corte dividiam o mesmo
 * martelo genérico. Com um catálogo único, equipar uma ferramenta é o mesmo
 * gesto em qualquer um dos três lugares, e o desenho identifica o equipamento.
 *
 * O atalho de uma tecla é a parte que faltava para a seleção parecer a de um
 * editor: equipar sem tirar a mão da cena. Ele fica no `title`, nunca no
 * `aria-label` — quem usa leitor de tela precisa ouvir o nome da ferramenta,
 * não uma letra solta.
 */
export interface FerramentaDaCaixa {
  id: FerramentaOficinaCorte
  /** Nome por extenso: o que o leitor de tela anuncia. */
  nome: string
  /** Nome curto, para as barras onde não cabe o nome inteiro. */
  nomeCurto: string
  /** Tecla única que equipa a ferramenta. */
  atalho: string
  Icone: ComponentType<PropsIcone>
  /** Onde a ferramenta atua. Vira a dica do botão e a mensagem ao equipar. */
  dica: string
}

export const FERRAMENTAS_DE_INTERVENCAO: readonly FerramentaDaCaixa[] = [
  { id: 'machadinha', nome: 'Machadinha', nomeCurto: 'Machadinha', atalho: 'M', Icone: IconeMachadinha, dica: 'Cria acesso ao corte do para-brisa na demonstração de teto lateral.' },
  { id: 'serraSabre', nome: 'Serra sabre', nomeCurto: 'Serra sabre', atalho: 'S', Icone: IconeSerraSabre, dica: 'Corta o para-brisa na demonstração de teto lateral.' },
  {
    id: 'cortadorHidraulico',
    nome: 'Cortador hidráulico',
    nomeCurto: 'Cortador',
    atalho: 'C',
    Icone: IconeCortadorHidraulico,
    dica: 'Corta dobradiças, limitador e fiação.',
  },
  {
    id: 'alargador',
    nome: 'Alargador',
    nomeCurto: 'Alargador',
    atalho: 'A',
    Icone: IconeAlargador,
    dica: 'Abre para expandir vínculos e esquadria; fecha para comprimir a maçaneta ou pinçar chapa e para-lama.',
  },
  {
    id: 'extensor',
    nome: 'Extensor',
    nomeCurto: 'Extensor',
    atalho: 'E',
    Icone: IconeExtensor,
    dica: 'Trabalha entre dois apoios: soleira e painel.',
  },
  {
    id: 'hooligan',
    nome: 'Barra Halligan',
    nomeCurto: 'Halligan',
    atalho: 'H',
    Icone: IconeHooligan,
    dica: 'Cria a abertura inicial na fresta ou quebra controlada de vidros com o espigão.',
  },
  {
    id: 'puncaoVidro',
    nome: 'Punção de Vidro com Mola (Holmatro)',
    nomeCurto: 'Punção',
    atalho: 'P',
    Icone: IconeVidro,
    dica: 'Apoie no canto do vidro, puxe a empunhadura e solte: a mola dispara e quebra o vidro.',
  },
]

/**
 * Equipamento de apoio que o material cataloga, mas que não intervém na peça.
 *
 * Fica visível — esconder faria o aluno pensar que a oficina ignora
 * estabilização — e fica desabilitado, porque equipá-lo deixava a cena num
 * estado em que pressionar qualquer ponto não produz nada.
 */
export const EQUIPAMENTOS_DE_APOIO: readonly FerramentaDaCaixa[] = [
  {
    id: 'vstrut',
    nome: 'V-Strut',
    nomeCurto: 'V-Strut',
    atalho: '',
    Icone: IconeVStrut,
    dica: 'Estabilização regulável em posições instáveis.',
  },
  {
    id: 'calcoEscalonado',
    nome: 'Calço escalonado',
    nomeCurto: 'Calço',
    atalho: '',
    Icone: IconeCalco,
    dica: 'Apoio sob a estrutura e anulação de folgas.',
  },
  {
    id: 'blocoCribbing',
    nome: 'Bloco',
    nomeCurto: 'Bloco',
    atalho: '',
    Icone: IconeBloco,
    dica: 'Preenchimento e apoio modular de vazios.',
  },
]

/** Por que o equipamento de apoio não é equipável na intervenção direta. */
export const MOTIVO_EQUIPAMENTO_DE_APOIO =
  'Equipamento de apoio: usado no módulo Estabilização, não na intervenção direta'

export interface ModoDaFerramenta {
  id: ModoAlargador
  nome: string
  /** Rótulo das barras estreitas, onde o nome inteiro empurra o resto da faixa. */
  nomeCurto: string
  Icone: ComponentType<PropsIcone>
  dica: string
}

/**
 * Modos do alargador — o equivalente às variantes escondidas atrás de uma
 * ferramenta num editor: a mesma ferramenta, dois gestos distintos. Por isso
 * a troca fica em `Shift` + a tecla da própria ferramenta.
 */
export const MODOS_DO_ALARGADOR: readonly ModoDaFerramenta[] = [
  {
    id: 'abrir',
    nome: 'Abrir',
    nomeCurto: 'Abrir',
    Icone: IconeAfastar,
    dica: 'Expande vínculos, esquadria e aberturas já criadas.',
  },
  {
    id: 'fechar',
    nome: 'Fechar / pinçar',
    nomeCurto: 'Fechar',
    Icone: IconeAproximar,
    dica: 'Pinça a chapa ou morde o para-lama dianteiro para criar acesso.',
  },
]

export const ATALHO_MODO_ALARGADOR = 'Shift+A'

/** A ferramenta cujo atalho é esta tecla, sem diferenciar maiúscula de minúscula. */
export function ferramentaPorAtalho(tecla: string): FerramentaDaCaixa | undefined {
  const alvo = tecla.toUpperCase()
  return FERRAMENTAS_DE_INTERVENCAO.find((ferramenta) => ferramenta.atalho === alvo)
}

export function ferramentaDaCaixa(id: FerramentaOficinaCorte): FerramentaDaCaixa | undefined {
  return [...FERRAMENTAS_DE_INTERVENCAO, ...EQUIPAMENTOS_DE_APOIO].find((f) => f.id === id)
}

/** Dica do botão: nome, atalho e o que a ferramenta faz — ou por que não dá. */
export function dicaDaFerramenta(ferramenta: FerramentaDaCaixa, motivoIndisponivel?: string): string {
  const nome = ferramenta.atalho ? `${ferramenta.nome} (${ferramenta.atalho})` : ferramenta.nome
  return `${nome} — ${motivoIndisponivel ?? ferramenta.dica}`
}
